import { Typography } from '@mui/material';
import { CustomCellEditorProps } from 'ag-grid-react';
import { ChangeEvent, useEffect } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { RendererType } from '../../../data-models/field-definition';
import { isNumericRenderer, isTextRenderer } from '../../../data-models/field2.data-model';
import { cellEditorWrapperCss } from '../../../theme/gridStyle';
import { isNumber, isPrintableCharacter } from '../../../util/stringUtils';
import { IFormField } from '../../../view-models/form.view-model';
import { FieldFactory } from '../../Form/FieldFactory';

export interface FieldCellEditorProps {
  formField: IFormField<unknown>;
  doOnChange?: (value: unknown) => void;
}

/**
 * A generic cell editor that renders form fields via FieldFactory.
 *
 * Note: For boolean fields, use `EditableBooleanFieldRenderer` instead.
 * Boolean toggles work best with single-click interaction, which bypasses
 * the edit mode
 */
export function FieldCellEditor(props: CustomCellEditorProps & FieldCellEditorProps) {
  const { value, formatValue, onValueChange, stopEditing, eventKey, formField, doOnChange } = props;

  // For singleSelect, wrap doOnChange to also stop editing after value selection.
  // This works with suppressKeyboardEvent to let MUI process Enter before we exit.
  const _doOnChange =
    formField.renderer === RendererType.singleSelect
      ? (newValue: unknown) => {
          doOnChange?.(newValue);
          stopEditing();
        }
      : doOnChange;

  const formProps = {
    onChange: getChangeHandlerForField(onValueChange, formField, _doOnChange),
    onBlur: stopEditing,
    value,
  } as ControllerRenderProps;

  useEffect(() => {
    if (formField.disabled) {
      return;
    }
    // Ag grid does not initialize the value to the first key pressed by default, eventKey can be a non printable
    // character - deal with this here.
    const renderer = formField.renderer;
    if (eventKey != null) {
      if (isNumericRenderer(renderer) && isNumber(eventKey)) {
        onValueChange(eventKey);
      } else if (isTextRenderer(renderer) && isPrintableCharacter(eventKey)) {
        onValueChange(eventKey);
      }
    }
  }, [eventKey, onValueChange, formField.renderer, formField.disabled]);

  if (formField.disabled) {
    return (
      <Typography component='span' variant='body2' color='text.secondary'>
        {formatValue(value)}
      </Typography>
    );
  }
  const field = getFormFieldWithMeta({ formField: { ...formField, autoFocus: true } });

  if (!field) {
    return <div>{value}</div>;
  }

  return (
    <div css={cellEditorWrapperCss}>
      <FieldFactory formField={field} formProps={formProps} />
    </div>
  );
}

function getChangeHandlerForField(
  onValueChange: (value: unknown) => void,
  formField: IFormField<unknown>,
  doOnChange?: (value: unknown) => void
) {
  if (formField.renderer === RendererType.text) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      onValueChange(e.target?.value ?? '');
      doOnChange?.(e.target?.value ?? '');
    };
  }
  if (doOnChange) {
    return (newValue: unknown) => {
      onValueChange(newValue);
      doOnChange(newValue);
    };
  }
  return onValueChange;
}

function getFormFieldWithMeta<T extends { formField: IFormField<unknown> }>(params: T) {
  if (!params.formField) {
    return null;
  }
  if (params.formField.renderer === RendererType.date) {
    return {
      ...params.formField,
      rendererMeta: { ...(params.formField.rendererMeta ?? {}), gridEditor: true },
      fullWidth: true,
    };
  }
  return params.formField;
}
